import React, { useState, useEffect, useRef } from 'react';
import { getAllMenus } from 'app/services/api';
import { useParams } from 'react-router-dom';

export default function MenuSelector({ label, selectedMenus, onAdd, onRemove, icon = 'add', readonly = false }) {
  const [searchValue, setSearchValue] = useState('');
  const [menuList, setMenuList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const dropdownRef = useRef(null);
  const { menuId } = useParams();

  useEffect(() => {
    const loadMenus = async () => {
      try {
        const res = await getAllMenus({ exceptIds: [menuId] });
        setMenuList(res.data.salonMenus || []);
      } catch (error) {
        console.error('Error loading menus:', error);
      }
    };
    loadMenus();
  }, []);

  useEffect(() => {
    if (searchValue.trim()) {
      const filtered = menuList.filter(
        (menu) =>
          menu.name.toLowerCase().includes(searchValue.toLowerCase()) &&
          !selectedMenus.some((selected) => selected._id === menu._id),
      );
      setFilteredMenus(filtered);
      setShowDropdown(true);
    } else {
      setFilteredMenus(menuList);
    }
  }, [searchValue, menuList, selectedMenus, showDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectMenu = (menu) => {
    onAdd(menu);
    setSearchValue('');
    setShowDropdown(false);
  };

  const handleIconClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleRemoveMenu = (menuId) => {
    onRemove(menuId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchValue.trim()) {
        const matchingMenu = menuList.find((menu) => menu.name.toLowerCase() === searchValue.toLowerCase());

        if (matchingMenu) {
          onAdd(matchingMenu);
        }
        setSearchValue('');
        setShowDropdown(false);
      }
    }
  };

  const IconComponent =
    icon === 'add' ? (
      <span className="material-symbols-outlined text-slate-400">add_circle</span>
    ) : (
      <span className="material-symbols-outlined text-slate-400">remove_circle</span>
    );

  return (
    <label className="block">
      <p className="text-sm font-bold mb-2">{label}</p>

      {!readonly && (
        <>
          <div className="relative" ref={dropdownRef}>
            <div className="flex items-center justify-between border border-slate-200 rounded-xl h-12 px-4 bg-white active:bg-slate-50 transition-colors">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="メニューを選択"
                className="flex-1 outline-none text-sm font-medium text-slate-700 placeholder-slate-400 bg-transparent"
              />
              <button
                type="button"
                onClick={handleIconClick}
                className="ml-auto hover:opacity-70 transition-opacity leading-none"
              >
                {IconComponent}
              </button>
            </div>

            {showDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {filteredMenus.length > 0 ? (
                  filteredMenus.map((menu) => (
                    <div
                      key={menu._id}
                      onClick={() => handleSelectMenu(menu)}
                      className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 transition-colors"
                    >
                      <p className="text-sm font-medium text-slate-900">{menu.name}</p>
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-3 text-sm text-slate-400">該当するメニューがありません</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {selectedMenus.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-2">
          {!readonly ? (
            <>
              {selectedMenus.map((menu) => (
                <span
                  key={menu._id}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                >
                  <span className="max-h-9 overflow-y-auto break-all default-scrollbar">{menu.name}</span>
                  <span
                    onClick={() => handleRemoveMenu(menu._id)}
                    className="material-symbols-outlined !text-sm cursor-pointer"
                  >
                    close
                  </span>
                </span>
              ))}
            </>
          ) : (
            <>
              {selectedMenus.map((menu) => (
                <span
                  key={menu._id}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 text-xs rounded-full"
                >
                  <span className="max-h-9 overflow-y-auto break-all default-scrollbar">{menu.name}</span>
                </span>
              ))}
            </>
          )}
        </div>
      ) : (
        <p>なし</p>
      )}
    </label>
  );
}
